/**
 * Azure cloud configuration for the DC-PROD environment.
 *
 * The desktop app is local-first: it works fully offline against local SQLite and
 * only talks to Azure through the backend API (never directly to Azure SQL/Blob).
 * These values are read from Vite env vars (see `.env.example`) and fall back to
 * the live DC-PROD resources provisioned in resource group `DC-PROD` (Central US).
 *
 * Cloud sync stays OFF unless `VITE_CLOUD_SYNC_ENABLED=true`, so nothing here
 * changes offline behavior or incurs cost until explicitly enabled.
 */

import { isTauri } from '../ai/tauri';

const env = import.meta.env;

export interface CloudConfig {
  /** Whether cloud login / sync features are enabled at all. */
  syncEnabled: boolean;
  /** Base URL of the .NET backend API (Azure Container Apps). */
  apiBaseUrl: string;
  /** Microsoft Entra External ID (CIAM) settings for user auth. */
  auth: {
    /** e.g. dcprodext.onmicrosoft.com */
    tenantDomain: string;
    /** Entra External ID tenant id. */
    tenantId: string;
    /** OIDC authority used by MSAL. */
    authority: string;
    /** App registration client id (created in the External ID tenant). */
    clientId: string;
    /** Scope the desktop app requests to call the backend API. */
    apiScope: string;
  };
  /** Blob storage account (uploads/audio/video/exports). Access is brokered by the API. */
  storageAccount: string;
}

export const cloudConfig: CloudConfig = {
  syncEnabled: env.VITE_CLOUD_SYNC_ENABLED === 'true',
  apiBaseUrl:
    env.VITE_API_BASE_URL ??
    'https://dc-prod-api.ashyglacier-16906c34.centralus.azurecontainerapps.io',
  auth: {
    tenantDomain: env.VITE_ENTRA_TENANT_DOMAIN ?? 'dcprodext.onmicrosoft.com',
    tenantId: env.VITE_ENTRA_TENANT_ID ?? '3f043d85-e908-46a8-bc08-158ed854ee2c',
    authority:
      env.VITE_ENTRA_AUTHORITY ??
      'https://dcprodext.ciamlogin.com/dcprodext.onmicrosoft.com',
    clientId: env.VITE_ENTRA_CLIENT_ID ?? '23266bb7-ef11-4dcf-9787-8509ff9476dd',
    apiScope:
      env.VITE_ENTRA_API_SCOPE ??
      'api://c6cf6dcf-0eb3-41c6-b99d-205ff9bbbf70/access_as_user',
  },
  storageAccount: env.VITE_STORAGE_ACCOUNT ?? 'dcprodstoreaf6fea',
};

/**
 * True on the hosted web portal (browser build with cloud sync enabled).
 * Hosted-web users get Foundry-backed cloud AI and always-on sync, so they
 * never see Codex/CLI configuration, sync toggles, or prompt-engineering
 * screens. Desktop (Tauri) and local dev builds stay local-first.
 */
export function isHostedWeb(): boolean {
  return !isTauri() && cloudConfig.syncEnabled;
}

/** Convenience helper to build an API endpoint URL. */
export function apiUrl(path: string): string {
  const base = cloudConfig.apiBaseUrl.replace(/\/$/, '');
  return `${base}/${path.replace(/^\//, '')}`;
}
